import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react'

const BOTTOM_THRESHOLD_PX = 24
const SUBPIXEL_PX = 1
const SCROLL_UP_KEYS = new Set(['PageUp', 'ArrowUp', 'Home'])

type StickToBottom = {
  /** Re-engages pinning and scrolls to the bottom. */
  stickToBottom: () => void
}

/**
 * Keeps a scroll container pinned to its bottom while content grows, until the user scrolls away.
 *
 * Pinning runs in a layout effect after every render and in a `ResizeObserver` callback on `contentRef`, both
 * before paint, so growth that does not come from a render (an animating disclosure, a late-loading image) is
 * followed too. A wheel-up, an upward key, or a `scroll` event that moves `scrollTop` up (scrollbar drag, touch)
 * disengages pinning, however close to the bottom it lands, unless the content got shorter at the same time (the
 * browser clamping `scrollTop` after a shrink). Scrolling down into a small threshold of the bottom, or calling
 * `stickToBottom`, re-engages it.
 */
function useStickToBottom(
  containerRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>
): StickToBottom {
  const isEngaged = useRef(true)
  const lastScrollTop = useRef(0)
  const lastScrollHeight = useRef(0)

  const pin = useCallback(() => {
    const container = containerRef.current
    if (!container || !isEngaged.current) return
    container.scrollTop = container.scrollHeight
    lastScrollTop.current = container.scrollTop
    lastScrollHeight.current = container.scrollHeight
  }, [containerRef])

  useLayoutEffect(() => {
    pin()
  })

  useEffect(() => {
    const content = contentRef.current
    if (!content || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(pin)
    observer.observe(content)
    return () => observer.disconnect()
  }, [contentRef, pin])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const disengage = () => {
      isEngaged.current = false
    }
    const handleWheel = (event: WheelEvent) => {
      if (
        event.deltaY < 0 &&
        container.scrollHeight > container.clientHeight &&
        !isHandledInside(event.target, container)
      ) {
        disengage()
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (SCROLL_UP_KEYS.has(event.key) && !isEditable(event.target) && !isHandledInside(event.target, container)) {
        disengage()
      }
    }
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const movedUp = scrollTop < lastScrollTop.current - SUBPIXEL_PX
      // Shrinking content makes the browser clamp scrollTop down; that drop is not the reader.
      const isClamp = movedUp && scrollHeight < lastScrollHeight.current
      if (movedUp && !isClamp) {
        disengage()
      } else if (
        !movedUp &&
        scrollTop >= lastScrollTop.current &&
        scrollHeight - clientHeight - scrollTop <= BOTTOM_THRESHOLD_PX
      ) {
        isEngaged.current = true
      }
      lastScrollTop.current = scrollTop
      lastScrollHeight.current = scrollHeight
    }

    container.addEventListener('wheel', handleWheel, { passive: true })
    container.addEventListener('keydown', handleKeyDown)
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('wheel', handleWheel)
      container.removeEventListener('keydown', handleKeyDown)
      container.removeEventListener('scroll', handleScroll)
    }
  }, [containerRef])

  const stickToBottom = useCallback(() => {
    isEngaged.current = true
    pin()
  }, [pin])

  return { stickToBottom }
}

/**
 * Whether an upward wheel or key event aimed at `target` is consumed by a nested scroller between `target` and
 * `container`: a vertical scroller that can still scroll up takes the event, so the container does not move.
 */
function isHandledInside(target: EventTarget | null, container: HTMLElement): boolean {
  for (let node = target instanceof Element ? target : null; node && node !== container; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    const isScroller = (overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight
    if (isScroller && node.scrollTop > 0) return true
  }
  return false
}

/** Whether keys aimed at `target` edit text or move a caret rather than scroll. */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

export default useStickToBottom
